<script setup>
import { computed, ref, watch, onMounted, onUnmounted, toRef } from 'vue';
import {
  FEATHER_PATHS,
  FEATHER_LOADER_VARIANTS,
  useFeatherLoaderMotion,
} from 'shared/composables/copilot/useFeatherLoaderMotion';
import './copilotFeatherLoader.css';

const props = defineProps({
  variant: {
    type: String,
    default: 'idle',
    validator: value => FEATHER_LOADER_VARIANTS.includes(value),
  },
  compact: { type: Boolean, default: false },
  animated: { type: Boolean, default: true },
  /** When false, swap variants directly without idle settle (avoids flash in message row). */
  settleOnChange: { type: Boolean, default: true },
});

const featherRef = ref(null);
const variantRef = toRef(props, 'variant');
const animatedRef = toRef(props, 'animated');

const effectiveVariant = computed(() => {
  if (!props.animated || props.variant === 'idle') return 'idle';
  return props.variant;
});

// Settle through idle on variant change so animations always restart from rest.
const renderVariant = ref(effectiveVariant.value);

const { start, stop, resetCycle, applyRest } = useFeatherLoaderMotion(
  featherRef,
  () => renderVariant.value,
  () => animatedRef.value
);

const isSearch = computed(
  () => renderVariant.value === 'search' && props.animated
);
const isWriting = computed(
  () => renderVariant.value === 'write' || renderVariant.value === 'draft'
);

const tipRightStageClass = computed(() =>
  props.compact
    ? 'copilot-feather-tip-right flex items-end justify-end overflow-visible pb-0.5'
    : 'copilot-feather-tip-right flex h-8 w-9 items-end justify-end'
);

const rootClass = computed(() => {
  if (props.compact) {
    return isSearch.value ? 'w-5 overflow-visible' : 'w-3.5 overflow-visible';
  }
  return isSearch.value ? 'h-8 w-6' : 'h-8 w-9';
});

const searchViewportClass = computed(() =>
  props.compact ? 'h-[18px] w-5' : 'h-8 w-6'
);

const iconClass = computed(() =>
  props.compact
    ? 'copilot-feather h-[18px] w-[18px] shrink-0 text-n-brand'
    : 'copilot-feather h-5 w-5 shrink-0 text-n-brand'
);

const featherClass = computed(() =>
  props.compact
    ? 'copilot-feather h-[18px] w-[18px] overflow-visible text-n-brand'
    : 'copilot-feather h-6 w-6 overflow-visible text-n-brand'
);

const SEARCH_ROTATIONS = [-18, -4, 10, 24, -18];

let settleFrame = null;

function scheduleVariant(target) {
  if (settleFrame) cancelAnimationFrame(settleFrame);

  if (target === renderVariant.value) return;

  if (target === 'idle') {
    renderVariant.value = 'idle';
    return;
  }

  renderVariant.value = 'idle';
  applyRest();

  settleFrame = requestAnimationFrame(() => {
    settleFrame = requestAnimationFrame(() => {
      renderVariant.value = target;
      resetCycle();
      settleFrame = null;
    });
  });
}

onMounted(() => {
  renderVariant.value = effectiveVariant.value;
  start();
});

onUnmounted(() => {
  if (settleFrame) cancelAnimationFrame(settleFrame);
  stop();
});

watch(effectiveVariant, target => {
  if (target === renderVariant.value) return;

  if (!props.settleOnChange) {
    renderVariant.value = target;
    return;
  }

  scheduleVariant(target);
});
</script>

<template>
  <div
    class="flex shrink-0 pointer-events-none"
    :class="[
      rootClass,
      compact ? 'items-end justify-end' : 'items-start justify-center',
      { 'copilot-feather-loader--compact': compact },
    ]"
  >
    <div
      v-if="isSearch"
      class="flex items-center overflow-hidden"
      :class="searchViewportClass"
    >
      <div
        :key="`search-${renderVariant}`"
        class="copilot-feather-search-track"
        aria-hidden="true"
      >
        <div
          v-for="(rotation, index) in SEARCH_ROTATIONS"
          :key="index"
          class="copilot-feather-tip-right shrink-0"
        >
          <svg
            :class="iconClass"
            :style="{ transform: `rotate(${rotation}deg)` }"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path :d="FEATHER_PATHS.nib" />
            <path :d="FEATHER_PATHS.shaft" />
            <path :d="FEATHER_PATHS.barb" />
          </svg>
        </div>
      </div>
    </div>
    <div v-else :class="tipRightStageClass">
      <svg
        ref="featherRef"
        :class="[
          featherClass,
          `copilot-feather-loader--${renderVariant}`,
          { 'copilot-feather--writing': isWriting },
        ]"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path class="nib" pathLength="1" :d="FEATHER_PATHS.nib" />
        <path class="shaft" pathLength="1" :d="FEATHER_PATHS.shaft" />
        <path class="barb" pathLength="1" :d="FEATHER_PATHS.barb" />
      </svg>
    </div>
  </div>
</template>
